import { memo, useEffect, useState, useCallback, useMemo } from 'react';
import { Tooltip, message, Space, Button, Table, Popover, Tag } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  CopyOutlined,
  ClearOutlined,
  FilterFilled,
} from '@ant-design/icons';
import { PiCheckBold, PiXBold } from 'react-icons/pi';
import CollectItemFilterPopup, { FilterOptions } from '../components/CollectItemFilterPopup/CollectItemFilterPopup';
import {
  convertFiltersToStrapiQuery,
  convertSortToStrapiQuery,
  formatStrapiQueryPreview,
  ColumnFilterRule,
  ColumnSortRule,
  ColumnFilterFieldType,
  STRAPI_OPERATOR_SHORT_LABELS,
  isColumnFilterRuleActive,
} from '~/utils/strapiFilters';
import { ColumnsType, TableProps } from 'antd/es/table';
import MobileCardList from '../components/MobileCardList';
import { useScreenSize } from '~/hooks/useScreenSize';
import {
  CollectItemAPIs,
  CollectItem_GetSyncablePage_RequestQuery,
  CollectItem_GetSyncablePage_Response,
  CollectItem_GetListWithUUIDs_ResponseItem,
  CollectItem_Export_RequestQuery,
} from '~/common/typescript/apis/CollectItemAPIs';
import {
  CollectFormAPIs,
} from '~/common/typescript/apis/CollectFormAPIs';
import { StreamSaverUtils } from '~/utils/StreamSaverUtils';
import { API_URL_ADMIN } from '~/config/config';
import GeometryPreview from '../components/GeometryPreview';
import { zoomToFeature } from '../utils/MapHighlightUtils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { userProfileModalAtom } from '~/components/Map/components/UserProfile/UserProfileStore';
import { projectMembersWithRolesAtom, projectMembersWithRolesAsyncAtom } from '~/components/Map/Member/MemberStore';
import { currentProjectAtom } from '~/stores/ProjectStore';
import {
  refreshCollectItemsAtom,
  collectItemPopupPropsAtom,
  geocollectItemPropertiesModeAtom,
} from '~/components/Map/controllers/popups/CollectItemPopup';
import {
  selectedFormUUIDAtom,
  availableFormsAtom,
  selectedFormDataAtom,
  isMaximizedAtom,
  activeFormColumnConfigsAtom,
  resetActiveFormColumnConfigsAtom,
} from '../atoms/CollectFormAtoms';
import styled from 'styled-components';
import DateTimeViewer from '~/components/common/DateTimeViewer';
import UUIDViewer from '~/components/common/UUIDViewer';
import BulkActionsBar from '../components/BulkActionsBar';
import FormListPanel from './CollectData/FormListPanel';
import Toolbar from './CollectData/Toolbar';
import ExportModal from './CollectData/ExportModal';
import { CollectColumnConfigItem } from './CollectData/ColumnSettingsPopover';
import ColumnHeaderControl, { SelectOptionItem } from './CollectData/ColumnHeaderControl';
import { parseFormContent, findTitleField } from '../utils/CollectFormParser';
import FieldTypeIcon from '../utils/FieldTypeIcon';

interface CollectDataTabProps {
  collectFormUuid?: string;
  tableHeight?: string;
  onMaximize?: () => void;
  onClose?: () => void;
}

const CollectDataTab = ({
  collectFormUuid,
  tableHeight,
  onMaximize,
  onClose,
}: CollectDataTabProps) => {
  const currentProject = useAtomValue(currentProjectAtom);
  const setUserProfileModal = useSetAtom(userProfileModalAtom);
  const refreshTrigger = useAtomValue(refreshCollectItemsAtom);
  const setCollectItemPopupProps = useSetAtom(collectItemPopupPropsAtom);
  const setGeocollectItemPropertiesMode = useSetAtom(geocollectItemPropertiesModeAtom);
  const [selectedFormUUID] = useAtom(selectedFormUUIDAtom);
  const [availableForms, setAvailableForms] = useAtom(availableFormsAtom);
  const selectedFormData = useAtomValue(selectedFormDataAtom);
  const isMaximized = useAtomValue(isMaximizedAtom);
  const { isMobile } = useScreenSize();
  const [collectItems, setCollectItems] = useState<CollectItem_GetListWithUUIDs_ResponseItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState<string | null>(null); // uuid of item being confirmed for deletion
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    pageSize: isMobile ? 15 : 10, // Increase page size for mobile
    pageCount: 0,
    total: 0,
  });

  // Filter & Sort states (Strapi query format)
  const [filterPopupVisible, setFilterPopupVisible] = useState(false);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>({});
  const [columnFilters, setColumnFilters] = useState<Record<string, ColumnFilterRule>>({});
  const [columnSort, setColumnSort] = useState<ColumnSortRule | null>(null);

  const memberWithRoles = useAtomValue(projectMembersWithRolesAtom);
  const fetchMembers = useSetAtom(projectMembersWithRolesAsyncAtom);

  useEffect(() => {
    if (memberWithRoles.length === 0) {
      fetchMembers();
    }
  }, [memberWithRoles.length, fetchMembers]);

  const userSelectOptions: SelectOptionItem[] = useMemo(() => {
    return memberWithRoles
      .map((m: any) => ({
        value: m.user?.id,
        label: m.user?.fullname || m.user?.username || `User #${m.user?.id}`,
      }))
      .filter((item) => item.value != null);
  }, [memberWithRoles]);

  // Multiselect states
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // Fetch available forms
  useEffect(() => {
    const fetchAvailableForms = async () => {
      if (!currentProject?.id) return;

      try {
        const response = await CollectFormAPIs.remote_CollectForm_getPage({
          projectId: currentProject.id,
          page: 1,
          pageSize: 100,
        });

        setAvailableForms(response.results);
      } catch (error) {
        console.error('Error fetching available forms:', error);
      }
    };

    fetchAvailableForms();
  }, [currentProject?.id]);

  const strapiFiltersObject = useMemo(
    () => convertFiltersToStrapiQuery(appliedFilters, columnFilters),
    [appliedFilters, columnFilters],
  );

  const strapiSortString = useMemo(
    () => convertSortToStrapiQuery(columnSort),
    [columnSort],
  );

  const strapiQueryPreview = useMemo(
    () => formatStrapiQueryPreview(strapiFiltersObject, strapiSortString),
    [strapiFiltersObject, strapiSortString],
  );

  const fetchCollectItems = useCallback(async () => {
    if (!selectedFormUUID) return;

    setLoading(true);
    try {
      // First get syncable page to get UUIDs (filtered and sorted on server)
      const syncableRequest: CollectItem_GetSyncablePage_RequestQuery = {
        collect_form_uuid: selectedFormUUID,
        page: paginationInfo.page,
        pageSize: paginationInfo.pageSize,
        no_deleted: true,
        filters: Object.keys(strapiFiltersObject).length > 0 ? strapiFiltersObject : undefined,
        sort: strapiSortString,
      };

      const syncableResponse: CollectItem_GetSyncablePage_Response =
        await CollectItemAPIs.remote_getPageSyncable(syncableRequest);

      if (syncableResponse.results.length > 0) {
        // Get detailed collect items with UUIDs
        const uuids = syncableResponse.results.map((item: { uuid: string }) => item.uuid);
        const detailedItems = await CollectItemAPIs.remote_getCollectItemsWithUUIDs({
          collect_form_uuid: selectedFormUUID,
          uuids: uuids,
        });

        // Preserve exact server sort order of uuids
        const itemMap = new Map(detailedItems.map((item) => [item.uuid, item]));
        const orderedItems = uuids
          .map((u) => itemMap.get(u))
          .filter((item): item is CollectItem_GetListWithUUIDs_ResponseItem => Boolean(item));

        setCollectItems(orderedItems);
      } else {
        setCollectItems([]);
      }

      setPaginationInfo((prev) => ({
        ...prev,
        total: syncableResponse.pagination.total,
        pageCount: syncableResponse.pagination.pageCount,
      }));
    } catch (error) {
      console.error('Error fetching collect items:', error);
    } finally {
      setLoading(false);
    }
  }, [
    selectedFormUUID,
    paginationInfo.page,
    paginationInfo.pageSize,
    strapiFiltersObject,
    strapiSortString,
  ]);

  // Reset filters/sort/page when selected form changes
  useEffect(() => {
    setColumnFilters({});
    setColumnSort(null);
    setAppliedFilters({});
    setPaginationInfo((prev) => ({ ...prev, page: 1 }));
  }, [selectedFormUUID]);

  // Since we're now using server-side filtering, we use collectItems directly
  const filteredCollectItems = collectItems;

  const highlightOnMap = (record: CollectItem_GetListWithUUIDs_ResponseItem) => {
    console.log('Zooming to feature on map:', record);

    if (record.geom) {
      try {
        const geometry = JSON.parse(record.geom);
        zoomToFeature(geometry);
      } catch (error) {
        console.error('Error parsing geometry:', error);
      }
    } else {
      console.warn('No geometry data available for this record');
    }
  };

  // Convert data for mobile card list
  const mobileCardData = filteredCollectItems.map((item) => {
    let lat: number | undefined;
    let lng: number | undefined;

    if (item.geom) {
      try {
        const geometry = JSON.parse(item.geom);

        if (geometry?.coordinates) {
          if (geometry.type === 'Point' && Array.isArray(geometry.coordinates)) {
            // Point: [lng, lat]
            const [longitude, latitude] = geometry.coordinates;
            if (typeof longitude === 'number' && typeof latitude === 'number') {
              lng = longitude;
              lat = latitude;
            }
          } else if (geometry.coordinates[0] && Array.isArray(geometry.coordinates[0])) {
            // Polygon/LineString: [[lng, lat], ...] or [[[lng, lat], ...]]
            const firstCoord = Array.isArray(geometry.coordinates[0][0])
              ? geometry.coordinates[0][0]
              : geometry.coordinates[0];
            if (firstCoord && typeof firstCoord[0] === 'number' && typeof firstCoord[1] === 'number') {
              lng = firstCoord[0];
              lat = firstCoord[1];
            }
          }
        }
      } catch (error) {
        console.error('Error parsing geometry coordinates:', error);
      }
    }

    // Get title from form content data
    let title = 'Dữ liệu thu thập';
    const titleField = findTitleField(selectedFormData);

    if (titleField) {
      const value = item.properties?.[titleField.key];
      if (value && value !== '') {
        title = String(value).substring(0, 50);
      } else {
        title = `${titleField.label} (trống)`;
      }
    }

    // Get form name from selectedFormData or availableForms
    const formName =
      selectedFormData?.name || availableForms.find((form) => form.uuid === item.collect_form_uuid)?.name || 'Không rõ';

    return {
      id: item.uuid || '',
      title: title,
      description: undefined, // Remove description to clean up card
      creator: item.user?.fullname || item.user?.username || 'Không rõ',
      creatorId: item.user?.id?.toString(),
      formName: formName,
      lat: lat,
      lng: lng,
      createdAt: item.client_created_at,
      syncAt: item.sync_at,
      geom: item.geom, // Add geometry data for preview
      properties: item.properties,
      user: item.user,
      uuid: item.uuid,
      collect_form_uuid: item.collect_form_uuid,
    };
  });

  // Edit and delete functionality - moved before useMemo
  const handleEditClick = useCallback(
    (item: CollectItem_GetListWithUUIDs_ResponseItem) => {
      let propsObj: any = item.properties;
      if (typeof propsObj === 'string') {
        try {
          propsObj = JSON.parse(propsObj);
        } catch {
          propsObj = {};
        }
      }
      // Open GeocollectItemPanel with the item data
      setCollectItemPopupProps({
        title: 'Sửa dữ liệu',
        properties: {
          uuid: item.uuid,
          collect_form_uuid: item.collect_form_uuid,
          ...propsObj,
        },
        lngLat: undefined, // We don't have coordinates from table click
      });

      // Set mode to EDIT
      setGeocollectItemPropertiesMode('EDIT');
    },
    [setCollectItemPopupProps, setGeocollectItemPropertiesMode],
  );

  const handleDelete = useCallback(
    async (uuid: string) => {
      try {
        await CollectItemAPIs.remote_delete(uuid);
        message.success('Đã xóa dữ liệu thành công');
        await fetchCollectItems(); // Refresh list
      } catch (error) {
        console.error('Error deleting collect item:', error);
        message.error('Lỗi khi xóa dữ liệu');
      }
    },
    [fetchCollectItems],
  );

  const handleDeleteConfirm = useCallback(
    (uuid: string) => {
      setDeleteConfirmVisible(null); // Hide confirm popover
      handleDelete(uuid);
    },
    [handleDelete],
  );

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmVisible(null);
  }, []);

  // Jotai persisted column configs per form (backed by atomWithStorage in CollectFormAtoms)
  const [columnConfigs, setColumnConfigs] = useAtom(activeFormColumnConfigsAtom);
  const resetColumnConfigs = useSetAtom(resetActiveFormColumnConfigsAtom);

  const handleChangeColumnConfigs = useCallback(
    (nextColumns: CollectColumnConfigItem[]) => {
      setColumnConfigs(nextColumns);
    },
    [setColumnConfigs],
  );

  const handleResetColumnConfigs = useCallback(() => {
    resetColumnConfigs();
  }, [resetColumnConfigs]);

  const handleApplyColumnFilter = useCallback(
    (columnKey: string, rule: ColumnFilterRule | null) => {
      setColumnFilters((prev) => {
        const next = { ...prev };
        if (!rule || !isColumnFilterRuleActive(rule)) {
          delete next[columnKey];
        } else {
          next[columnKey] = rule;
        }
        return next;
      });
      setPaginationInfo((prev) => ({ ...prev, page: 1 }));
    },
    [],
  );

  const handleChangeColumnSort = useCallback((sort: ColumnSortRule | null) => {
    setColumnSort(sort);
    setPaginationInfo((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleClearAllQuery = useCallback(() => {
    setColumnFilters({});
    setColumnSort(null);
    setAppliedFilters({});
    setPaginationInfo((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Generate dynamic columns based on form content schema & user column settings
  const columns: ColumnsType<CollectItem_GetListWithUUIDs_ResponseItem> = useMemo(() => {
    const columnMetaMap: Record<
      string,
      {
        label: string;
        titleNode: React.ReactNode;
        isSystemColumn?: boolean;
        sortableFilterable?: boolean;
        fieldType?: ColumnFilterFieldType;
        selectOptions?: SelectOptionItem[];
      }
    > = {
      uuid: {
        label: 'UUID',
        titleNode: <span>UUID</span>,
        isSystemColumn: true,
        sortableFilterable: true,
        fieldType: 'text',
      },
      preview: {
        label: 'Hình học',
        titleNode: <span>Hình học</span>,
        isSystemColumn: true,
        sortableFilterable: false,
      },
      sync_at: {
        label: 'Ngày đồng bộ',
        titleNode: <span>Ngày đồng bộ</span>,
        isSystemColumn: true,
        sortableFilterable: true,
        fieldType: 'date',
      },
      user: {
        label: 'Người tạo',
        titleNode: <span>Người tạo</span>,
        isSystemColumn: true,
        sortableFilterable: true,
        fieldType: 'user',
        selectOptions: userSelectOptions,
      },
    };

    const columnMap: Record<string, ColumnsType<CollectItem_GetListWithUUIDs_ResponseItem>[number]> = {
      uuid: {
        title: 'UUID',
        dataIndex: 'uuid',
        key: 'uuid',
        width: 120,
        render: (uuid: string) => <UUIDViewer uuid={uuid} />,
      },
      preview: {
        title: 'Hình học',
        dataIndex: 'geom',
        key: 'preview',
        width: 80,
        render: (geom: string) => {
          try {
            const geometry = geom ? JSON.parse(geom) : null;
            return geometry ? (
              <GeometryPreview geometry={geometry} width={60} height={40} />
            ) : (
              <span style={{ fontSize: '10px', color: '#ccc' }}>No geom</span>
            );
          } catch (error) {
            return <span style={{ fontSize: '10px', color: '#ccc' }}>Invalid</span>;
          }
        },
      },
      sync_at: {
        title: 'Ngày đồng bộ',
        dataIndex: 'sync_at',
        key: 'sync_at',
        width: 145,
        render: (syncAt: string) => (
          <span style={{ fontSize: '12px' }}>
            {syncAt ? <DateTimeViewer date={syncAt} initialMode='relative' /> : <span style={{ color: '#ccc' }}>-</span>}
          </span>
        ),
      },
      user: {
        title: 'Người tạo',
        dataIndex: 'user',
        key: 'user',
        width: 140,
        render: (user: any) => (
          <CreatorName
            onClick={(e) => {
              e.stopPropagation();
              if (user?.id) {
                setUserProfileModal({
                  visible: true,
                  userId: user.id,
                });
              }
            }}
          >
            {user?.fullname || user?.username || 'Không rõ'}
          </CreatorName>
        ),
      },
    };

    const parsedContent = parseFormContent(selectedFormData);
    if (parsedContent && parsedContent.fields.length > 0) {
      parsedContent.fields.forEach((field) => {
        const fieldType = (field.type || '').toLowerCase().trim();
        const isNumberField =
          fieldType === 'number' ||
          fieldType === 'integer' ||
          fieldType === 'float' ||
          fieldType === 'double' ||
          fieldType === 'decimal' ||
          fieldType === 'numeric';

        const isDateField =
          fieldType === 'date' ||
          fieldType === 'datetime' ||
          fieldType === 'time' ||
          field.key.toLowerCase().includes('date') ||
          field.key.toLowerCase().includes('time');

        const isBooleanField = fieldType === 'boolean' || fieldType === 'checkbox';
        const isSelectField =
          fieldType === 'select' || (Array.isArray(field.options) && field.options.length > 0);

        const filterFieldType: ColumnFilterFieldType = isNumberField
          ? 'number'
          : isDateField
            ? 'date'
            : isBooleanField
              ? 'boolean'
              : isSelectField
                ? 'select'
                : 'text';

        const selectOptions: SelectOptionItem[] = Array.isArray(field.options)
          ? field.options.map((opt: any) => ({
              label: opt?.label ?? opt?.value ?? String(opt),
              value: opt?.value ?? opt?.label ?? String(opt),
            }))
          : [];

        const titleNode = (
          <span style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
            <FieldTypeIcon type={field.type} fieldKey={field.key} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {field.label || field.key}
            </span>
          </span>
        );

        columnMetaMap[field.key] = {
          label: field.label || field.key,
          titleNode,
          isSystemColumn: false,
          sortableFilterable: true,
          fieldType: filterFieldType,
          selectOptions,
        };

        columnMap[field.key] = {
          title: titleNode,
          key: field.key,
          width: 155,
          align: isNumberField ? 'right' : undefined,
          render: (_, record: any) => {
            let propsObj = record.properties;
            if (typeof propsObj === 'string') {
              try {
                propsObj = JSON.parse(propsObj);
              } catch {
                propsObj = {};
              }
            }
            let value = propsObj?.[field.key];

            if (!value && value !== 0) {
              return (
                <span
                  style={{
                    color: '#ccc',
                    ...(isNumberField
                      ? { fontFamily: 'monospace', display: 'block', textAlign: 'right' }
                      : {}),
                  }}
                >
                  -
                </span>
              );
            }

            if (isDateField) {
              const dateVal =
                typeof value === 'string' && !isNaN(Number(value)) && Number(value) > 1000000000000
                  ? Number(value)
                  : value;
              return (
                <span style={{ fontSize: '12px' }}>
                  <DateTimeViewer date={dateVal} initialMode="relative" />
                </span>
              );
            }

            if (typeof value === 'boolean' || fieldType === 'boolean') {
              return (
                <span style={{ fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  {value ? (
                    <>
                      <PiCheckBold style={{ color: 'var(--color-success, #22c55e)' }} /> Có
                    </>
                  ) : (
                    <>
                      <PiXBold style={{ color: 'var(--color-danger, #ef4444)' }} /> Không
                    </>
                  )}
                </span>
              );
            }

            if (isNumberField || typeof value === 'number') {
              const numVal = typeof value === 'number' ? value : Number(value);
              const formatted =
                !isNaN(numVal) && String(value).trim() !== ''
                  ? numVal.toLocaleString('vi-VN')
                  : String(value);
              return (
                <span
                  style={{
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    fontVariantNumeric: 'tabular-nums',
                    display: 'block',
                    textAlign: 'right',
                  }}
                >
                  {formatted}
                </span>
              );
            }

            return <span style={{ fontSize: '12px' }}>{String(value)}</span>;
          },
        };
      });
    }

    const visibleConfigs = columnConfigs.filter((c) => (c.visible || c.locked) && columnMap[c.key]);
    const pinnedConfigs = visibleConfigs.filter((c) => c.pinned);
    const unpinnedConfigs = visibleConfigs.filter((c) => !c.pinned);
    const effectiveConfigs = [...pinnedConfigs, ...unpinnedConfigs];

    const orderedDataColumns: ColumnsType<CollectItem_GetListWithUUIDs_ResponseItem> = effectiveConfigs.map(
      (cfg) => {
        const baseDef = columnMap[cfg.key];
        const meta = columnMetaMap[cfg.key];
        const colDef = { ...baseDef };

        if (cfg.pinned) {
          colDef.fixed = 'left';
        } else {
          colDef.fixed = undefined;
        }

        if (meta && meta.sortableFilterable) {
          colDef.title = (
            <ColumnHeaderControl
              columnKey={cfg.key}
              label={meta.label}
              titleNode={meta.titleNode}
              isSystemColumn={meta.isSystemColumn}
              isPinned={cfg.pinned}
              fieldType={meta.fieldType}
              selectOptions={meta.selectOptions}
              activeFilter={columnFilters[cfg.key]}
              activeSort={columnSort}
              onApplyFilter={handleApplyColumnFilter}
              onChangeSort={handleChangeColumnSort}
            />
          );
        }

        return colDef;
      },
    );

    const actionColumn: ColumnsType<CollectItem_GetListWithUUIDs_ResponseItem>[number] = {
      title: 'Hành động',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record: CollectItem_GetListWithUUIDs_ResponseItem) => (
        <Space size="middle">
          <Tooltip title="Sửa">
            <Button
              disabled={false}
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(record);
              }}
            />
          </Tooltip>
          <Popover
            content={
              <div style={{ width: 180 }}>
                <div style={{ marginBottom: 8 }}>Bạn có chắc chắn muốn xóa?</div>
                <Space>
                  <Button size="small" onClick={() => handleDeleteConfirm(record.uuid)} danger>
                    Xóa
                  </Button>
                  <Button size="small" onClick={handleDeleteCancel}>
                    Hủy
                  </Button>
                </Space>
              </div>
            }
            title="Xác nhận xóa"
            trigger="click"
            open={deleteConfirmVisible === record.uuid}
            onOpenChange={(visible) => setDeleteConfirmVisible(visible ? record.uuid : null)}
          >
            {/* Xóa */}
            <Button disabled={false} type="text" size="small" icon={<DeleteOutlined />} danger />
          </Popover>
        </Space>
      ),
    };

    return [...orderedDataColumns, actionColumn];
  }, [
    columnConfigs,
    selectedFormData,
    setUserProfileModal,
    deleteConfirmVisible,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleEditClick,
    userSelectOptions,
    columnFilters,
    columnSort,
    handleApplyColumnFilter,
    handleChangeColumnSort,
  ]);

  useEffect(() => {
    fetchCollectItems();
  }, [fetchCollectItems]);

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchCollectItems();
    }
  }, [refreshTrigger, fetchCollectItems]);

  // Filter functions
  const handleApplyFilter = (filters: FilterOptions) => {
    setAppliedFilters(filters);
    setPaginationInfo((prev) => ({ ...prev, page: 1 }));
  };

  const activeColumnFilterList = useMemo(
    () => Object.values(columnFilters).filter(isColumnFilterRuleActive),
    [columnFilters],
  );

  const hasModalFilters = useMemo(() => {
    if (appliedFilters.searchText && appliedFilters.searchText.trim() !== '') return true;
    if (appliedFilters.dateRange && appliedFilters.dateRange.length === 2) return true;
    if (appliedFilters.syncDateRange && appliedFilters.syncDateRange.length === 2) return true;
    if (appliedFilters.creatorIds && appliedFilters.creatorIds.length > 0) return true;
    if (
      appliedFilters.selectedFields &&
      Object.values(appliedFilters.selectedFields).some((v) => v && String(v).trim() !== '')
    ) {
      return true;
    }
    return false;
  }, [appliedFilters]);

  const formatFilterValueLabel = (rule: ColumnFilterRule): string => {
    if (rule.operator === '$null' || rule.operator === '$notNull') return '';
    if (rule.fieldType === 'user') {
      const ids = Array.isArray(rule.value) ? rule.value : [rule.value];
      return ids
        .map((id) => {
          const found = userSelectOptions.find((u) => Number(u.value) === Number(id));
          return found ? found.label : `#${id}`;
        })
        .join(', ');
    }
    if (Array.isArray(rule.value)) {
      if (rule.operator === '$between' && rule.value.length === 2) {
        if (rule.fieldType === 'date') {
          return `${String(rule.value[0]).slice(0, 10)} → ${String(rule.value[1]).slice(0, 10)}`;
        }
        return `${rule.value[0]} → ${rule.value[1]}`;
      }
      return rule.value.join(', ');
    }
    if (rule.fieldType === 'date' && typeof rule.value === 'string') {
      return rule.value.slice(0, 10);
    }
    return String(rule.value ?? '');
  };

  // Multiselect functions
  const handleBulkDelete = useCallback(async () => {
    if (selectedRowKeys.length === 0) return;

    setBulkDeleteLoading(true);
    try {
      const response = await CollectItemAPIs.remote_bulkDelete({
        uuids: selectedRowKeys as string[],
      });
      message.success(`Đã xóa ${response.data.deleted_count} bản ghi thành công`);
      setSelectedRowKeys([]);
      await fetchCollectItems();
    } catch (error: any) {
      console.error('Error bulk deleting collect items:', error);
      message.error(error?.response?.data?.message || 'Lỗi khi xóa dữ liệu');
    } finally {
      setBulkDeleteLoading(false);
    }
  }, [selectedRowKeys, fetchCollectItems]);

  const handleBulkDownload = useCallback(async (format: 'xlsx' | 'geojson' | 'csv' | 'shp') => {
    if (!collectFormUuid || selectedRowKeys.length === 0) return;

    const formatMap: Record<string, 'ESRI Shapefile' | 'MapInfo File' | 'LIBKML' | 'XLSX' | 'GeoJSON'> = {
      xlsx: 'XLSX',
      geojson: 'GeoJSON',
      csv: 'GeoJSON',
      shp: 'ESRI Shapefile',
    };

    const exportFormat = formatMap[format];
    const extensions: Record<string, string> = {
      'ESRI Shapefile': 'zip',
      'MapInfo File': 'zip',
      LIBKML: 'kml',
      XLSX: 'xlsx',
      GeoJSON: 'geojson',
    };

    try {
      const request: CollectItem_Export_RequestQuery = {
        form_uuid: collectFormUuid,
        format: exportFormat,
        uuids: selectedRowKeys as string[],
      };

      const response = await CollectItemAPIs.remote_exportCollectItems(request);

      const extension = extensions[exportFormat];
      const filename = `collect_items_selected_${Date.now()}.${extension}`;

      const downloadUrl = `${API_URL_ADMIN}${response.data.download_url}`;
      await StreamSaverUtils.download(downloadUrl, filename);

      message.success(`Đã tải xuống ${selectedRowKeys.length} bản ghi`);
    } catch (error: any) {
      console.error('Export error:', error);
      if (error?.response?.status === 404) {
        message.warning(
          error?.response?.data?.i18n?.vi || 'Không tìm thấy dữ liệu để xuất',
        );
      } else {
        message.error(error?.response?.data?.message || error?.response?.data?.i18n?.vi || 'Lỗi khi xuất dữ liệu');
      }
    }
  }, [collectFormUuid, selectedRowKeys]);

  const handleSelectAll = useCallback(() => {
    const allKeys = filteredCollectItems.map((item) => item.uuid);
    setSelectedRowKeys(allKeys);
  }, [filteredCollectItems]);

  const handleClearSelection = useCallback(() => {
    setSelectedRowKeys([]);
  }, []);

  // Clear selection when form changes
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [collectFormUuid]);

  const rowSelection: TableProps<CollectItem_GetListWithUUIDs_ResponseItem>['rowSelection'] = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  };

  if (!selectedFormUUID) {
    return (
      <FormListPanel
        onMaximize={onMaximize}
        onClose={onClose}
        isMaximized={isMaximized}
      />
    );
  }

  return (
    <Container>
      <Toolbar
        isMaximized={isMaximized}
        loading={loading}
        collectItemsCount={collectItems.length}
        recordCount={paginationInfo.total}
        onOpenFilter={() => setFilterPopupVisible(true)}
        onOpenExport={() => setExportModalVisible(true)}
        onRefresh={fetchCollectItems}
        onMaximize={onMaximize}
        onClose={onClose}
        columnConfigs={columnConfigs}
        onChangeColumnConfigs={handleChangeColumnConfigs}
        onResetColumnConfigs={handleResetColumnConfigs}
      />

      {(activeColumnFilterList.length > 0 || columnSort !== null || hasModalFilters) && (
        <QueryStatusBar data-testid="strapi-query-status-bar">
          <QueryTagsLeft>
            <QueryBarLabel>
              <FilterFilled style={{ color: '#2563eb', fontSize: 11 }} />
              <span>Truy vấn:</span>
            </QueryBarLabel>

            {columnSort && (
              <Tag
                color="geekblue"
                closable
                onClose={() => handleChangeColumnSort(null)}
                style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                {columnSort.direction === 'asc' ? (
                  <SortAscendingOutlined />
                ) : (
                  <SortDescendingOutlined />
                )}
                <span>
                  Sắp xếp: <strong>{columnSort.label || columnSort.columnKey}</strong> (
                  {columnSort.direction.toUpperCase()})
                </span>
              </Tag>
            )}

            {activeColumnFilterList.map((rule) => {
              const valText = formatFilterValueLabel(rule);
              return (
                <Tag
                  key={rule.columnKey}
                  color="blue"
                  closable
                  onClose={() => handleApplyColumnFilter(rule.columnKey, null)}
                  style={{ margin: 0 }}
                >
                  <strong>{rule.label || rule.columnKey}</strong>{' '}
                  <span style={{ opacity: 0.85 }}>
                    [{STRAPI_OPERATOR_SHORT_LABELS[rule.operator] || rule.operator}]
                  </span>
                  {valText ? `: ${valText}` : ''}
                </Tag>
              );
            })}

            {hasModalFilters && (
              <Tag
                color="cyan"
                closable
                onClose={() => setAppliedFilters({})}
                style={{ margin: 0 }}
              >
                Bộ lọc nâng cao
              </Tag>
            )}
          </QueryTagsLeft>

          <QueryActionsRight>
            {strapiQueryPreview && (
              <Tooltip title={`URL Query: ?${strapiQueryPreview} (Nhấn để sao chép)`}>
                <Button
                  data-testid="btn-copy-strapi-query"
                  type="text"
                  size="small"
                  icon={<CopyOutlined style={{ fontSize: 11 }} />}
                  style={{ fontSize: 11, height: 22, padding: '0 6px', color: '#475569' }}
                  onClick={() => {
                    navigator.clipboard.writeText(strapiQueryPreview);
                    message.success('Đã sao chép URL Query');
                  }}
                >
                  URL Query
                </Button>
              </Tooltip>
            )}
            <Button
              data-testid="btn-clear-all-query"
              type="link"
              size="small"
              danger
              icon={<ClearOutlined style={{ fontSize: 11 }} />}
              style={{ fontSize: 11, height: 22, padding: '0 4px' }}
              onClick={handleClearAllQuery}
            >
              Hủy lọc
            </Button>
          </QueryActionsRight>
        </QueryStatusBar>
      )}

      {!isMobile && selectedRowKeys.length > 0 && (
        <BulkActionsBar
          selectedCount={selectedRowKeys.length}
          totalCount={filteredCollectItems.length}
          onBulkDelete={handleBulkDelete}
          onDownload={handleBulkDownload}
          onClearSelection={handleClearSelection}
          onSelectAll={handleSelectAll}
          loading={bulkDeleteLoading}
        />
      )}

      {isMobile ? (
        <MobileCardList
          data={mobileCardData}
          loading={loading}
          onItemClick={(item) => {
            const collectItem = collectItems.find((c) => c.uuid === item.id);
            if (collectItem) highlightOnMap(collectItem);
          }}
          onCreatorClick={(creatorId) => {
            setUserProfileModal({
              visible: true,
              userId: parseInt(creatorId),
            });
          }}
          onEditClick={(item) => {
            const collectItem = collectItems.find((c) => c.uuid === item.id);
            if (collectItem) handleEditClick(collectItem);
          }}
          onDeleteClick={(item) => {
            handleDeleteConfirm(item.id);
          }}
          pagination={{
            current: paginationInfo.page,
            pageSize: paginationInfo.pageSize,
            total: paginationInfo.total,
            onChange: (page, pageSize) => {
              setPaginationInfo({ ...paginationInfo, page, pageSize });
            },
          }}
          formData={selectedFormData ?? undefined}
        />
      ) : (
        <Table
          dataSource={filteredCollectItems}
          columns={columns}
          rowKey={'uuid'}
          rowSelection={rowSelection}
          bordered
          style={{
            cursor: 'pointer',
            flex: 1,
            width: '100%',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #f1f5f9',
          }}
          onRow={(record) => {
            return {
              onClick: () => {
                highlightOnMap(record);
              },
            };
          }}
          size="small"
          loading={loading}
          scroll={{ y: tableHeight ? `calc(${tableHeight} - 20px)` : undefined, x: '100%' }}
          pagination={{
            current: paginationInfo.page,
            pageSize: paginationInfo.pageSize,
            total: paginationInfo.total,
            pageSizeOptions: [10, 20, 50, 100],
            position: ['bottomCenter'],
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total}`,
            onChange: (page, pageSize) => {
              setPaginationInfo({ ...paginationInfo, page, pageSize });
            },
          }}
        />
      )}

      {/* Export Modal */}
      <ExportModal
        visible={exportModalVisible}
        onClose={() => setExportModalVisible(false)}
        collectFormUuid={selectedFormUUID}
        collectItemsCount={collectItems.length}
      />

      {/* Filter Popup */}
      <CollectItemFilterPopup
        visible={filterPopupVisible}
        onClose={() => setFilterPopupVisible(false)}
        onApplyFilter={handleApplyFilter}
        formData={selectedFormData ?? undefined}
      />
    </Container>
  );
};

export default memo(CollectDataTab);

const Container = styled.div`
  padding: 6px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 4px;
  }

  .ant-table-thead > tr > th {
    font-size: 10px !important;
    padding: 4px !important;
  }

  .ant-table-tbody > tr > td {
    padding: 4px !important;
  }

  .ant-table-measure-row td {
    padding: 0 !important;
  }
`;

const CreatorName = styled.span`
  font-size: 12px;
  color: #3b82f6;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }
`;

const QueryStatusBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 10px;
  margin-bottom: 6px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  flex-wrap: wrap;
`;

const QueryTagsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
`;

const QueryBarLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #1e40af;
`;

const QueryActionsRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;
